import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'
import KEYCODE from 'keycode'

import highlightSearchTerm from './highlightSearchTerm'

class AutoSuggest extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      inputValue: props.inputValue,
      isOpen: false,
      focusedIndex: -1
    }

    this.handleInputFocus = this.handleInputFocus.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleKeyUp = this.handleKeyUp.bind(this)
    this.setWrapperRef = this.setWrapperRef.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleMenuMouseEnter = this.handleMenuMouseEnter.bind(this)
    this.handleMenuMouseLeave = this.handleMenuMouseLeave.bind(this)
  }

  componentDidMount () {
    document.addEventListener('click', this.handleDocumentClick, true)
    document.addEventListener('touchend', this.handleDocumentClick, true)
  }

  componentWillUnmount () {
    document.removeEventListener('click', this.handleDocumentClick, true)
    document.removeEventListener('touchend', this.handleDocumentClick, true)
  }

  componentWillReceiveProps (nextProps) {
    if (nextProps.inputValue !== this.props.inputValue) {
      this.setState({
        inputValue: nextProps.inputValue
      })
    }
  }

  setWrapperRef (ref) {
    if (ref !== null) {
      this.wrapper = ref
    }
  }

  handleDocumentClick (event) {
    if (!this.wrapper.contains(event.target)) {
      this.setState({ isOpen: false })
    }
  }

  handleInputChange (event) {
    if (this.props.onInputChange) {
      this.props.onInputChange(event.target.value)
    }

    // if auto suggest menu is not open, we should open it on input change
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true
      })
    }
  }

  handleInputFocus (event) {
    this.setState({
      isOpen: true
    })
  }

  onMenuClick (option, event) {
    event.preventDefault()

    if (this.props.onSelectOption) {
      this.props.onSelectOption(option, event)
    }

    this.setState({
      isOpen: false
    })
  }

  handleKeyDown (event) {
    const keyCode = event.which

    switch (keyCode) {
      // Block up/down arrow from moving the cursor in the search input
      case KEYCODE('up'):
      case KEYCODE('down'):
        event.preventDefault()
        break

      // 1. Because Enter triggers form submission before we get the chance to run
      //    `_updateAnalyticsParamInput`, we disable the submission on keydown and
      //    manually trigger it on keyup. Ideally we'd have the form and the input
      //    as custom React components and then this wouldn't be necessary.
      // 2. Block Esc from emptying the search input
      case KEYCODE('enter'):
      case KEYCODE('esc'):
        event.preventDefault()
        break
    }
  }

  handleKeyUp (event) {
    const { focusedIndex, inputValue } = this.state
    const { suggestions } = this.props

    const keyCode = event.which

    switch (keyCode) {
      case KEYCODE('enter'):
        if (this.props.onSelectOption) {
          // when the user hit `enter` without selecting any option, we simply pass the inputValue to onSelection option
          this.props.onSelectOption(focusedIndex === -1 ? inputValue : suggestions[focusedIndex], event)
        }

        this.setState({
          isOpen: false
        })
        break

      // Esc hides autosuggest and deselects the current suggestion
      case KEYCODE('esc'):
        this.setState({
          isOpen: false,
          focusedIndex: -1
        })
        break

      case KEYCODE('up'):
        this.setFocusedIndex('prev')
        break

      case KEYCODE('down'):
        this.setFocusedIndex('next')
        break

      default:
        this.setState({
          focusedIndex: -1
        })
        event.preventDefault()
    }
  }

  setFocusedIndex (dir) {
    const { focusedIndex, inputValue } = this.state
    const { suggestions } = this.props
    const optionLength = suggestions.length

    const isNoneSelected = (focusedIndex === -1)
    const isFirstSelected = (focusedIndex === 0)
    const isLastSelected = (focusedIndex === optionLength - 1)

    if (dir === 'prev') {
      if (isFirstSelected || isNoneSelected) {
        this.setState({
          focusedIndex: optionLength - 1,
          inputValue: suggestions[optionLength - 1] ? suggestions[optionLength - 1].text : inputValue
        })
      } else {
        this.setState({
          focusedIndex: focusedIndex - 1,
          inputValue: suggestions[focusedIndex - 1] ? suggestions[focusedIndex - 1].text : inputValue
        })
      }
    } else if (dir === 'next') {
      if (isLastSelected || isNoneSelected) {
        this.setState({
          focusedIndex: 0,
          inputValue: suggestions[0] ? suggestions[0].text : inputValue
        })
      } else {
        this.setState({
          focusedIndex: focusedIndex + 1,
          inputValue: suggestions[focusedIndex + 1] ? suggestions[focusedIndex + 1].text : inputValue
        })
      }
    }
  }

  handleMenuMouseEnter (index) {
    this.setState({
      focusedIndex: index
    })
  }

  handleMenuMouseLeave (index) {
    this.setState({
      focusedIndex: -1
    })
  }

  renderWrapper () {
    const { suggestions } = this.props
    const { focusedIndex, inputValue } = this.state

    /**
     * Hack inline style :magic:
     */
    const wrapperStyle = {
      position: 'absolute',
      top: this.textInput.clientHeight,
      width: this.wrapper.clientWidth
    }

    return (
      <div className='autosuggest__wrapper -border-radius-bottom' style={wrapperStyle}>
        <section ref={(ref) => { this.menu = ref }} className='autosuggest__suggestions'>
          <ul className='autosuggest__list fds-c-text-list -style-none'>
            {suggestions.map((suggestion, index) => {
              return (
                <li
                  className={classNames('autosuggest__list-item', {
                    focused: focusedIndex === index
                  })}
                  onMouseDown={(event) => this.onMenuClick(suggestion, event)}
                  onMouseEnter={(event) => this.handleMenuMouseEnter(index)}
                  onMouseLeave={(event) => this.handleMenuMouseLeave(index)}
                  key={index}
                >
                  <a
                    className='fds-c-text-link -color-dark -decoration-none'
                    href={suggestion.link}
                  >
                    <span className='autosuggest__suggestion-text h-text-truncate'>
                      <span
                        dangerouslySetInnerHTML={{__html: highlightSearchTerm(suggestion.text, inputValue.trim())}} />
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </section>
      </div>
    )
  }

  render () {
    const { isOpen, inputValue } = this.state
    const { suggestions, inputName, placeholder, type, ariaLabel } = this.props

    const inputStyle = {
      borderRadius: isOpen && suggestions.length ? '7px 0 0 0' : '7px 0 0 7px'
    }

    const buttonStyle = {
      borderRadius: isOpen && suggestions.length ? '0 7px 0 0' : '0 7px 7px 0'
    }

    return (
      <div ref={this.setWrapperRef}>
        <input
          ref={(input) => { this.textInput = input }}
          style={inputStyle}
          placeholder={placeholder || ''}
          autoComplete='off'
          type={type || 'search'}
          aria-label={ariaLabel || 'search'}
          name={inputName || ''}

          value={inputValue}
          onChange={this.handleInputChange}
          onFocus={this.handleInputFocus}
          onKeyDown={this.handleKeyDown}
          onKeyUp={this.handleKeyUp}
        />
        <button className='fds-c-button huge-search--btn -theme-themeforest' style={buttonStyle} defaultValue='Submit'>
          <span className='e-icon__alt'>Search</span>
        </button>
        {isOpen && suggestions && suggestions.length > 0 &&
          this.renderWrapper()
        }
      </div>
    )
  }
}

AutoSuggest.propTypes = {
  suggestions: PropTypes.array.isRequired,
  onInputChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  onSelectOption: PropTypes.func.isRequired,
  inputName: PropTypes.string,
  inputValue: PropTypes.string.isRequired
}

export default AutoSuggest



// WEBPACK FOOTER //
// ./client/components/autosuggest/index.js